<template>
  <PVDialog
    v-model:visible="internalModel"
    :modal="true"
    :blockScroll="true"
    :showHeader="!hasActiveBlock"
    :closable="!hasActiveBlock"
    :closeOnEscape="!persistent && !hasActiveBlock"
    :dismissableMask="!persistent && !hasActiveBlock"
    :draggable="false"
    :pt="{
      mask: { class: 's-modal-backdrop-blur' },
    }"
    :contentStyle="hasActiveBlock ? { padding: '0', borderRadius: '12px', overflow: 'hidden' } : undefined"
    :style="{
      width: dialogWidth,
    }"
    :class="{ 's-modal-blocked': hasActiveBlock }"
    @hide="handleClose"
  >
    <!-- Cabeçalho Customizado -->
    <template #header>
      <div class="hidden m-0 p-0" v-if="hasActiveBlock" />

      <slot name="header" v-else>
        <div
          class="flex gap-2 justify-between w-full flex-wrap md:flex-row items-start md:items-center"
        >
          <div class="flex gap-2 items-center">
            <slot name="headerIcon">
              <i
                v-if="headerOptions?.icon"
                :class="headerOptions.icon"
                class="!text-2xl"
              />
            </slot>

            <div class="flex flex-col">
              <span v-if="headerOptions?.title" class="text-xl font-semibold">
                {{ headerOptions.title }}
              </span>

              <span
                v-if="headerOptions?.subtitle"
                class="text-sm font-normal text-gray-600 dark:text-gray-300"
              >
                {{ headerOptions?.subtitle }}
              </span>
            </div>
          </div>

          <slot name="headerComponent"></slot>

          <PVChip
            v-if="headerOptions?.chip"
            class="ml-auto text-sm"
            :label="headerOptions.chip"
          ></PVChip>
        </div>
      </slot>
    </template>

    <div :class="hasActiveBlock ? 'p-0' : 'p-6'">
      <div v-if="loading" class="flex justify-center py-12">
        <SLoadingSpinner :showMessage="true" />
      </div>

      <div v-else-if="hasActiveBlock" class="w-full flex flex-col m-0 p-0">
        <div
          class="bg-[#1a1a1a] text-white pt-12 pb-8 px-6 flex flex-col items-center justify-center"
        >
          <div
            class="w-14 h-14 rounded-full bg-[#2a2a2a] flex items-center justify-center mb-5"
          >
            <component
              v-if="isComponentIcon(activeBlockConfig?.icon)"
              :is="activeBlockConfig?.icon"
              size="24"
              :style="{ color: resolveIconColor(activeBlockConfig?.iconColor) }"
            />
            <i
              v-else
              :class="(activeBlockConfig?.icon as string) || 'pi pi-lock'"
              class="!text-xl"
              :style="{ color: resolveIconColor(activeBlockConfig?.iconColor) }"
            />
          </div>

          <h3 class="text-xl font-bold mb-1 text-center">Funcionalidade bloqueada</h3>
          <p v-if="blockedModuleLabel" class="text-gray-300 text-xs font-semibold text-center mb-3 uppercase tracking-wide">
            {{ blockedModuleLabel }}
          </p>
          <p class="text-gray-400 text-sm text-center max-w-[360px] leading-relaxed">
            {{ activeBlockConfig?.message }}
          </p>
        </div>

        <div class="bg-white px-6 py-6 flex flex-col gap-3">
          <PVButton
            v-if="primaryBlockedAction"
            @click="handleBlockAction(primaryBlockedAction)"
            class="bg-[#1a1a1a]! hover:bg-black! text-white! font-medium! px-4 py-3.5! rounded-lg! w-full! flex! justify-center! items-center! transition-colors!"
          >
            {{ primaryBlockedAction.text }}
          </PVButton>

          <PVButton
            @click="handleClose"
            class="bg-white! border border-gray-300! text-gray-800! hover:bg-gray-50! font-medium! px-4! py-3.5! rounded-lg! w-full! flex! justify-center! items-center! transition-colors!"
          >
            Fechar
          </PVButton>
        </div>
      </div>

      <!-- Conteúdo Normal -->
      <div v-else>
        <slot>Conteúdo do modal.</slot>
      </div>
    </div>

    <!-- Rodapé do Modal -->
    <template
      v-if="!hasActiveBlock && ($slots.footer || footerOptions)"
      #footer
    >
      <div class="w-full">
        <slot name="footer">
          <div class="flex flex-col justify-end align-end">
            <PVDivider class="my-5" />
            <div class="w-full flex md:flex-row flex-col gap-2">
              <SButton
                v-if="footerOptions?.cancelButton?.visible"
                :label="footerOptions?.cancelButton?.text || 'Cancelar'"
                :icon="footerOptions?.cancelButton?.icon"
                :class="footerOptions?.cancelButton?.class"
                :severity="footerOptions?.cancelButton?.severity || 'secondary'"
                :size="footerOptions?.cancelButton?.size"
                :variant="footerOptions?.cancelButton?.variant || 'filled'"
                :loading="footerOptions?.cancelButton?.loading"
                :disabled="footerOptions?.cancelButton?.disabled"
                @click="cancel"
              />
              <SButton
                v-if="footerOptions?.confirmButton?.visible"
                :label="footerOptions?.confirmButton?.text || 'Confirmar'"
                :icon="footerOptions?.confirmButton?.icon"
                :class="footerOptions?.confirmButton?.class"
                :severity="footerOptions?.confirmButton?.severity || 'primary'"
                :size="footerOptions?.confirmButton?.size"
                :variant="footerOptions?.confirmButton?.variant || 'filled'"
                :loading="footerOptions?.confirmButton?.loading"
                :disabled="footerOptions?.confirmButton?.disabled"
                @click="confirm"
              />
            </div>
          </div>
        </slot>
      </div>
    </template>
  </PVDialog>
</template>

<script lang="ts" setup>
import { computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useSessionStore } from '@/stores/components/sessionStore'
import { useSaldoConta } from '@/stores/components/useSaldoConta'
import { EmpresaStatusDocumentacaoEnum } from '@/enums/EmpresaStatusDocumentacaoEnum'
import {
  BlockAction,
  BlockConfig,
  Props,
} from '@/components/globals/SModalComponent/SModalComponent.types'
import { getModuloBlockLabel } from '@/constants/moduloBlockDisplay'

// Componentes locais
import SButton from '../SButton/SButton.vue'
import SLoadingSpinner from '@/components/globals/loading/SLoadingSpinner.vue'
import { DoorClosedLocked } from 'lucide-vue-next'
import type { Component } from 'vue'

const router = useRouter()
const sessionStore = useSessionStore()
const saldoStore = useSaldoConta()

// Tipos e Interfaces

const props = withDefaults(defineProps<Props>(), {
  checkSaldo: false,
})

const emits = defineEmits(['blocked'])

const internalModel = defineModel<boolean>()

const primaryBlockedAction = computed<BlockAction | null>(() => {
  return activeBlockConfig.value?.actions?.[0] || null
})

const blockedModuleLabel = computed<string | null>(() => {
  return getModuloBlockLabel(props.module)
})

const subscriptionDefaultBlockRule = computed<{
  message: string
  action: BlockAction | null
} | null>(() => {
  if (!sessionStore.isSubscriptionDefault) {
    return null
  }

  const empresaId = sessionStore.userData?.empresa?.uuid_emp_id
  const documentationRoute = empresaId
    ? `/cadastros/empresas/editar/${empresaId}`
    : null

  const documentacaoAprovada = Boolean(sessionStore.currentEmpresa?.emp_documentacao_status)
  const statusDocumentacao = sessionStore.currentEmpresa?.emp_status_documentacao

  if (!documentacaoAprovada && statusDocumentacao === EmpresaStatusDocumentacaoEnum.PENDENTE) {
    return {
      message:
        'Envie sua documentação para prosseguir com a liberação do acesso às funcionalidades.',
      action: documentationRoute
        ? {
            text: 'Enviar Documentação',
            action: 'route',
            route: documentationRoute,
            query: { tab: 'three' },
            color: 'contrast',
          }
        : null,
    }
  }

  if (
    !documentacaoAprovada &&
    statusDocumentacao === EmpresaStatusDocumentacaoEnum.AGUARDANDO_APROVACAO
  ) {
    return {
      message:
        'Aguarde a aprovação da sua documentação para contratar um plano e prosseguir com a liberação do acesso às funcionalidades.',
      action: null,
    }
  }

  if (!documentacaoAprovada && statusDocumentacao === EmpresaStatusDocumentacaoEnum.RECUSADO) {
    return {
      message:
        'Sua documentação foi recusada. Envie novamente para prosseguir com a liberação do acesso às funcionalidades.',
      action: documentationRoute
        ? {
            text: 'Reenviar Documentação',
            action: 'route',
            route: documentationRoute,
            query: { tab: 'three' },
            color: 'contrast',
          }
        : null,
    }
  }

  if (documentacaoAprovada) {
    return {
      message: 'Sua documentação foi aprovada. Contrate um plano para utilizar esta funcionalidade.',
      action: {
        text: 'Ver Planos',
        action: 'route',
        route: '/planos',
        color: 'contrast',
      },
    }
  }

  return {
    message:
      'Para utilizar as funcionalidades da plataforma, é necessário contratar um plano de assinatura.',
    action: {
      text: 'Ver Planos',
      action: 'route',
      route: '/planos',
      color: 'contrast',
    },
  }
})

const subscriptionDefaultBlock = computed<BlockConfig | null>(() => {
  if (
    !sessionStore.isSubscriptionDefault ||
    hasActiveCampaignForCurrentContext.value ||
    sessionStore.currentUser?.usu_seth
  ) {
    return null
  }

  const defaultRule = subscriptionDefaultBlockRule.value

  return {
    type: 'subscription',
    icon: DoorClosedLocked,
    iconColor: 'white',
    title: 'Funcionalidade bloqueada',
    message:
      defaultRule?.message ||
      'Para utilizar as funcionalidades da plataforma, é necessário contratar um plano de assinatura.',
    actions: defaultRule?.action ? [defaultRule.action] : [],
    condition: () => true,
  }
})

const contratoLicencaBlock = computed<BlockConfig | null>(() => {
  if (
    sessionStore.isSubscriptionDefault ||
    sessionStore.currentUser?.usu_seth ||
    sessionStore.isLegacyCompanyExemptFromContractBlock
  ) {
    return null
  }

  if (router.currentRoute.value.name === 'EditarEmpresa') {
    return null
  }

  const documentacao = sessionStore.currentEmpresa?.empresa_documentacao
  const contratosAssinados =
    Boolean(documentacao?.edo_contrato_principal_status) &&
    Boolean(documentacao?.edo_anexo_i_status)

  if (contratosAssinados) {
    return null
  }

  const empresaId = sessionStore.userData?.empresa?.uuid_emp_id

  return {
    type: 'documentation',
    icon: DoorClosedLocked,
    iconColor: 'white',
    title: 'Funcionalidade bloqueada',
    message:
      'A assinatura dos documentos de contrato e licença do plano é obrigatória.',
    actions: empresaId
      ? [
          {
            text: 'Ver contrato',
            action: 'route',
            route: `/cadastros/empresas/editar/${empresaId}`,
            query: { tab: 'three' },
            color: 'contrast',
          },
        ]
      : [],
    condition: () => true,
  }
})

const normalizeModuleList = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.map((moduleCode) => String(moduleCode).toUpperCase())
  }

  if (value) {
    return [String(value).toUpperCase()]
  }

  return []
}

const currentContextModules = computed(() => {
  const currentRoute = router.currentRoute.value
  const routeModules =
    currentRoute.meta?.module ??
    [...currentRoute.matched]
      .reverse()
      .find((record) => Boolean(record.meta?.module))?.meta?.module

  const normalizedRouteModules = normalizeModuleList(routeModules)
  if (normalizedRouteModules.length > 0) {
    return normalizedRouteModules
  }

  return normalizeModuleList(props.module)
})

function resolveMaybeRef<T>(maybeRef: unknown): T | null {
  if (maybeRef && typeof maybeRef === 'object' && 'value' in (maybeRef as any)) {
    return (maybeRef as any).value as T
  }
  return (maybeRef as T) ?? null
}

const hasActiveCampaignForCurrentContext = computed(() => {
  const campaings = resolveMaybeRef<any[]>(sessionStore.currentCampaings) ?? []

  if (!campaings.length || !currentContextModules.value.length) {
    return false
  }

  return campaings.some((campaign) => {
    if (!campaign || campaign.ativa === false || campaign.ativa === 0) {
      return false
    }

    if (!Array.isArray(campaign.modulos)) {
      return false
    }

    return campaign.modulos.some((moduleItem: any) =>
      currentContextModules.value.includes(
        String(moduleItem?.codigo ?? '').toUpperCase()
      )
    )
  })
})

// Configurações de bloqueio padrão
const defaultBlocks = computed<BlockConfig[]>(() => {
  const blocks: BlockConfig[] = []
  // Bloqueio por saldo insuficiente
  if (
    props.checkSaldo &&
    saldoStore.saldoBaixo &&
    !sessionStore.currentEmpresa?.emp_documentacao_status &&
    !sessionStore.isSubscriptionDefault 
    && !hasActiveCampaignForCurrentContext.value
  ) {
    blocks.push({
      type: 'saldo',
      icon: 'pi pi-wallet',
      iconColor: 'warning',
      title: 'Saldo Insuficiente',
      message: saldoStore.getMensagemSaldoInsuficiente(),
      actions: [
        {
          text: 'Enviar Documentação',
          action: 'route',
          route: `/cadastros/empresas/editar`,
          query: { tab: 'three' },
          color: 'contrast',
          icon: 'pi pi-file-edit',
        },
      ],
      condition: () => props.checkSaldo && saldoStore.saldoBaixo //&& !hasActiveCampaignForCurrentContext.value,
    })
  }

  /*if(
    sessionStore?.isSubscriptionDefault &&
    !sessionStore.userData?.empresa?.emp_documentacao_status &&
    !hasActiveCampaignForCurrentContext.value &&
    !sessionStore?.currentUser?.usu_seth
  ){
    const info = FatorDesativacaoInfo[FatorDesativacaoEnum.PLANO_PENDENTE]
    blocks.push({
      message: info.mensagem,
      actions: [
        {
          action: 'route',
          route: info.acao.rota,
          icon: info.acao.icon,
          color: 'contrast',
        },assdn 
      ],
      condition: () =>
        (sessionStore?.isSubscriptionDefault &&
          !sessionStore.userData?.empresa?.emp_documentacao_status &&
          !hasActiveCampaignForCurrentContext.value) ||
        false,
    })
  }*/
  return blocks
})

// Combina todos os bloqueios
const allBlocks = computed<BlockConfig[]>(() => {
  return [
    ...(subscriptionDefaultBlock.value ? [subscriptionDefaultBlock.value] : []),
    ...(contratoLicencaBlock.value ? [contratoLicencaBlock.value] : []),
    ...defaultBlocks.value,
    ...(props.customBlocks || []),
  ]
})

// Verifica qual bloqueio está ativo
const activeBlockConfig = computed<BlockConfig | null>(() => {
  return allBlocks.value.find((block) => block.condition?.() ?? false) || null
})

const hasActiveBlock = computed(() => !!activeBlockConfig.value)

const dialogWidth = computed(() => {
  if (!props.maxWidth) {
    return hasActiveBlock.value ? '500px' : undefined
  }

  if (typeof props.maxWidth === 'number' || /^\d+$/.test(String(props.maxWidth))) {
    return `${props.maxWidth}px`
  }

  return String(props.maxWidth)
})

const iconColorMap: Record<string, string> = {
  white: '#e5e7eb',
  warning: '#f59e0b',
  danger: '#ef4444',
  error: '#ef4444',
  info: '#3b82f6',
  success: '#10b981',
  primary: '#6366f1',
  secondary: '#94a3b8',
  orange: '#f97316',
  grey: '#9ca3af',
}

const resolveIconColor = (iconColor?: string): string => {
  if (!iconColor) {
    return iconColorMap.white
  }

  return iconColorMap[iconColor] || iconColor
}

const isComponentIcon = (icon: BlockConfig['icon'] | undefined): icon is Component => {
  return typeof icon !== 'string' && !!icon
}

watch(
  hasActiveBlock,
  (blocked) => {
    if (blocked && activeBlockConfig.value) {
      emits('blocked', activeBlockConfig.value)
    }
  },
  { immediate: true }
)

// Métodos
const handleBlockAction = (action: BlockAction) => {
  switch (action.action) {
    case 'route':
      if (action.route) {
        router.push({
          path: action.route,
          query: action.query,
        })
        handleClose()
      }
      break
    case 'callback':
      action.callback?.()
      break
    case 'close':
      handleClose()
      break
  }
}

const handleClose = () => {
  internalModel.value = false
  props.options?.onClose?.()
}

const cancel = () => {
  props.footerOptions?.cancelButton?.onClick?.()
}

const confirm = () => {
  props.footerOptions?.confirmButton?.onClick?.()
}
</script>

<style>
.s-modal-backdrop-blur {
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
  background: rgba(15, 23, 42, 0.18);
}
</style>

<style scoped>
/* Estilos adicionais se necessário */
:deep(.p-dialog-header) {
  border-radius: 0;
}

:deep(.s-modal-blocked .p-dialog-content) {
  padding: 0 !important;
}

:deep(.s-modal-blocked .p-dialog-header) {
  display: none !important;
}

:deep(.s-modal-blocked .p-dialog-content) {
  border-radius: 12px;
  overflow: hidden;
}

:deep(.s-modal-blocked .p-dialog) {
  border-radius: 12px;
  overflow: hidden;
}

</style>
